import { useNavigate } from "react-router-dom";
import { Plus, UserPlus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useChildProfile } from "../contexts/ChildProfileContext";
import { BigButton, Card } from "../design/BigButton";
import { avatarLabel, ASSETS } from "../lib/assets";

const MAX_PROFILES = 3;

export function ProfilesScreen() {
  const { profiles, select, loading } = useChildProfile();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  function pick(profileId) {
    select(profileId);
    navigate(`/child/${profileId}/home`);
  }

  function addProfile() {
    // Reuse the parent-dashboard Profiles Manager modal so behavior stays in
    // one place. `?add=1` opens the "Add child" form immediately.
    navigate("/parent/dashboard/profiles?add=1");
  }

  const canAddMore = !loading && profiles.length < MAX_PROFILES;

  return (
    <div className="wmleye-app profiles-screen" data-testid="profiles-screen">
      <header className="profiles-screen__header">
        <div>
          <h1 className="profiles-screen__title">Who&apos;s playing today?</h1>
          <p className="profiles-screen__sub">Signed in as {user?.email}</p>
        </div>
        <BigButton variant="ghost" onClick={logout} data-testid="profiles-logout">
          Sign out
        </BigButton>
      </header>
      {loading ? (
        <p>Loading…</p>
      ) : profiles.length === 0 ? (
        <Card className="profiles-screen__empty" data-testid="profiles-empty">
          <img
            src={ASSETS.mascot}
            alt="Ollie the owl"
            className="profiles-screen__empty-mascot"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <h2>Ready to start exploring?</h2>
          <p>Let&apos;s set up your first little explorer&apos;s profile — it only takes a minute.</p>
          <BigButton onClick={addProfile} data-testid="profiles-add-first">
            <UserPlus size={22} aria-hidden /> Add child profile
          </BigButton>
        </Card>
      ) : (
        <div className="profiles-screen__grid">
          {profiles.map((p) => (
            <button
              key={p.id}
              className="profile-tile"
              onClick={() => pick(p.id)}
              data-testid={`profile-tile-${p.nickname.toLowerCase()}`}
              aria-label={`Play as ${p.nickname}`}
            >
              <div className="profile-tile__avatar" aria-hidden>
                {avatarLabel(p.avatar)}
              </div>
              <div className="profile-tile__name">{p.nickname}</div>
              <div className="profile-tile__meta">
                {p.tokenBalance} tokens · {p.totalStars} ⭐
              </div>
            </button>
          ))}
          {canAddMore && (
            <button
              className="profile-tile profile-tile--add"
              onClick={addProfile}
              data-testid="profiles-add-tile"
              aria-label="Add another child profile"
            >
              <div className="profile-tile__avatar" aria-hidden>
                <Plus size={44} strokeWidth={3} />
              </div>
              <div className="profile-tile__name">Add child</div>
              <div className="profile-tile__meta">{profiles.length} of {MAX_PROFILES}</div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
